<template>
  <v-dialog v-model="show" persistent max-width="700px">
    <v-card :class="classContents">
      <v-card-title>
        <h3>{{ title }}</h3>
      </v-card-title>
      <v-form ref="form" v-model="valid">
        <v-row class="mx-3 mt-3">
          <v-col cols="6" class="pr-5">
            <v-card flat class="dialog-form" :class="classContents">
              <div class="d-flex flex-column">
                <div class="my-1">
                  <div class="float-left column-name">
                    <span class="d-flex align-center">{{ $t('system.id') }}</span>
                  </div>
                  <div class="float-left column-contents">
                    <v-text-field v-model="item.id" :disabled="readonly" :rules="[rules.required]" class="w-auto" outlined dense></v-text-field>
                  </div>
                </div>
                <div class="my-1">
                  <div class="float-left column-name">
                    <span class="d-flex align-center">{{ $t('system.system') }}</span>
                  </div>
                  <div class="float-left column-contents">
                    <v-select v-model="item.systemId" :disabled="readonly" :rules="[rules.required]" :items="systems" item-text="alias" item-value="id" class="item-select w-auto" :placeholder="this.$t('common.noSelect')" outlined dense></v-select>
                  </div>
                </div>
                <div class="my-1">
                  <div class="float-left column-name">
                    <span class="d-flex align-center">{{ $t('collector.restAddress') }}</span>
                  </div>
                  <div class="float-left column-contents">
                    <v-text-field v-model="item.restAddress" :rules="[rules.required]" class="w-auto" outlined dense></v-text-field>
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>
          <v-col cols="6">
            <v-card flat class="dialog-form" :class="classContents">
              <div class="d-flex flex-column">
                <div class="my-1">
                  <div class="float-left column-name empty"></div>
                </div>
                <div class="my-1">
                  <div class="float-left column-name">
                    <span class="d-flex align-center">{{ $t('collector.stackType') }}</span>
                  </div>
                  <div class="float-left column-contents">
                    <v-select ref="stackType" v-model="item.stackType" :disabled="readonly" :rules="[rules.required, rules.exists]" @change="onChangeStackType" :items="stackTypes" item-text="name" item-value="name" class="item-select w-auto" :placeholder="this.$t('common.noSelect')" outlined dense></v-select>
                  </div>
                </div>
                <div class="my-1">
                  <div class="float-left column-name">
                    <span class="d-flex align-center">{{ $t('collector.terminalAddress') }}</span>
                  </div>
                  <div class="float-left column-contents">
                    <v-text-field v-model="item.terminalAddress" :rules="[rules.required]" class="item-select w-auto" outlined dense></v-text-field>
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-form>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn small color="primary" @click="save" :loading="loading" :disabled="disabledSave">{{ $t('common.save') }}</v-btn>
        <v-btn small color="primary" @click="cancel">{{ $t('common.cancel') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { api } from '@/api/common/instances';
import validation from '@/utils/validation';
import { isEqual, merge } from 'lodash';
import bus from '@/utils/bus';
import { VUE_INSTANCE_NAME } from '@/constants';

let exists = false;

const defaultItem = {
  id: '',
  systemId: '',
  stackType: '',
  restAddress: '',
  terminalAddress: '',
};

let beforeChangeItem = {};

export default {
  props: {
    values: {
      type: Object,
      required: true,
    },
    value: Boolean,
  },
  data() {
    return {
      title: '',
      item: Object.assign({}, defaultItem),
      valid: true,
      validated: true,
      readonly: false,
      loading: false,
      systems: [],
      stackTypes: [],
      exists: false,
      rules: {
        exists: () => !exists || window[VUE_INSTANCE_NAME].$t('validation.registered'),
        ...validation,
      },
    };
  },
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    disabledSave() {
      if (beforeChangeItem.id) {
        return !this.valid || isEqual(beforeChangeItem, this.item);
      } else {
        return !this.valid;
      }
    },
    classContents() {
      return {
        'contents-background-dark': this.$vuetify.theme.isDark,
      };
    },
  },
  watch: {
    show: {
      immediate: true,
      handler: 'setDialogActivated',
    },
  },
  methods: {
    async save() {
      this.loading = true;
      const { status } = this.values.id ? await this.update() : await this.add();
      if (status == 200) {
        this.show = false;
        if (!beforeChangeItem.id) this.$emit('insert');
      }
      this.loading = false;
    },
    async add() {
      return await api.post('/preference/collectors', this.item);
    },
    async update() {
      const item = (() => {
        if (Object.prototype.hasOwnProperty.call(this.item, 'children')) {
          const item = Object.assign({}, this.item);
          delete item['children'];
          return item;
        } else {
          return this.item;
        }
      })();
      return await api.put(`/preference/collectors/${this.item.id}/${this.item.systemId}/${this.item.stackType}`, item);
    },
    cancel() {
      this.item = merge({}, beforeChangeItem);
      beforeChangeItem = {};
      this.show = false;
    },
    async setDialogActivated(show) {
      if (show) {
        if (this.values.id) {
          this.title = this.$t('collector.title.edit');
          this.readonly = true;
          beforeChangeItem = merge({}, this.values);
          this.item = this.values;
        } else {
          this.$nextTick(() => {
            this.title = this.$t('collector.title.add');
            this.readonly = false;
            beforeChangeItem = merge({}, defaultItem);
            this.item = merge({}, defaultItem);
            this.$refs.form.resetValidation();
          });
        }
      } else {
        this.item = merge({}, defaultItem);
        this.$refs.form.resetValidation();
      }
    },
    setEventListener() {
      bus.$on('response-exists', e => (exists = e));
    },
    removeEventListener() {
      bus.$off('response-exists');
    },
    onChangeStackType() {
      bus.$emit('request-exists', this.item);
    },
  },
  async created() {
    this.setEventListener();
    ({ data: this.systems } = await api.get('/preference/systems'));
    ({ data: this.stackTypes } = await api.get('/stacktypes'));
  },
  destroyed() {
    this.removeEventListener();
  },
};
</script>

<style scoped>
.empty {
  height: 50px;
}
</style>
<style>
.item-select .v-input__slot {
  height: 24px;
}
.v-input__append-inner {
  margin-top: 2px !important;
}
.v-input__prepend-inner {
  margin-top: 2px !important;
}
</style>
